// src/hooks/useData.js — Custom React Hooks
import { useState, useEffect, useCallback, useRef } from 'react';
import { market, trading, forums, notifications, alerts } from '../services/api';

// ─── Generic async hook ───────────────────────────────────
export function useAsync(asyncFn, deps = [], immediate = true) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(immediate);
  const [error,   setError]   = useState(null);

  const execute = useCallback(async (...args) => {
    setLoading(true);
    setError(null);
    try {
      const result = await asyncFn(...args);
      setData(result);
      return result;
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { if (immediate) execute(); }, [execute, immediate]);

  return { data, loading, error, execute, setData };
}

// ─── Live market prices ──────────────────────────────────────
// v196a FROZEN-TICKER FIX: WebSocket drives most updates, but REST polls
// every 30s as a safety net so the ticker NEVER freezes if WS fails to
// emit price_update events (e.g., backend marketDataGateway hasn't filled
// the cache yet, or socket reconnects mid-session).
export function useMarketPrices() {
  const [prices,  setPrices]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetchPrices = useCallback(async () => {
    try {
      const res = await market.prices();
      if (res.success) setPrices(res.data || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrices(); // initial
    // v196a: 30-second polling safety net (cheap REST call, prevents frozen ticker)
    const iv = setInterval(fetchPrices, 30000);
    return () => clearInterval(iv);
  }, [fetchPrices]);

  return { prices, loading, error, refetch: fetchPrices };
}

// ─── Single coin chart ────────────────────────────────────
// BUG FIX: Keep previous candles while fetching so chart never
// goes blank on coin/timeframe switch — fixes "No Chart Data" flash
export function useCoinChart(coinId, days = 1) {
  const [candles, setCandles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!coinId) return;
    setLoading(true);
    // NOTE: We do NOT clear candles here — old data stays visible while new loads
    market.chart(coinId, days)
      .then(res => { if (res.success && res.data?.length) setCandles(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [coinId, days]);

  return { candles, loading };
}

// ─── Portfolio ────────────────────────────────────────────
export function usePortfolio() {
  return useAsync(() => trading.portfolio());
}

// ─── User Stats (SINGLE SOURCE OF TRUTH) ─────────────────
// Use this for trade counts, PnL, daily limits — everywhere.
export function useUserStats() {
  return useAsync(() => trading.userStats());
}

// ─── P&L ──────────────────────────────────────────────────
export function usePnL() {
  return useAsync(() => trading.pnl());
}

// ─── Orders ───────────────────────────────────────────────
// FIX: JSON.stringify(params || {}) — previously JSON.stringify(undefined)
// returned the JS primitive undefined (not the string "undefined"), making
// the useCallback dep array contain [undefined] instead of ["{}"].
export function useOrders(params) {
  return useAsync(() => trading.orders(params), [JSON.stringify(params || {})]);
}

// ─── Watchlist ────────────────────────────────────────────
export function useWatchlist() {
  const [localList, setLocalList] = useState(null); // null = use server data
  const { data, loading, execute } = useAsync(() => trading.watchlist());
  const serverList = data?.watchlist || [];
  const list = localList !== null ? localList : serverList;

  // Sync local list when server data arrives
  useEffect(() => {
    if (data?.watchlist) setLocalList(data.watchlist);
  }, [data]);

  const add = async (symbol) => {
    const sym = symbol.toUpperCase();
    // Optimistic: add immediately
    setLocalList(prev => {
      const current = prev !== null ? prev : serverList;
      if (current.some(w => w.symbol?.toUpperCase() === sym)) return current;
      return [...current, { id: 'temp_' + sym, symbol: sym, created_at: new Date().toISOString() }];
    });
    try {
      await trading.addWatch(sym);
      execute(); // sync authoritative data
    } catch(e) {
      // Rollback on failure
      setLocalList(prev => (prev || []).filter(w => w.symbol?.toUpperCase() !== sym));
      throw e;
    }
  };

  const remove = async (symbol) => {
    const sym = symbol.toUpperCase();
    // Optimistic: remove immediately
    setLocalList(prev => (prev !== null ? prev : serverList).filter(w => w.symbol?.toUpperCase() !== sym));
    try {
      await trading.removeWatch(sym);
      execute(); // sync authoritative data
    } catch(e) {
      // Rollback on failure
      execute();
      throw e;
    }
  };

  return { watchlist: list, loading: loading && !localList, add, remove, refetch: execute };
}

// ─── Alerts ───────────────────────────────────────────────
export function useAlerts() {
  const { data, loading, execute } = useAsync(() => alerts.list());
  const list = data?.alerts || [];

  const create = async (alertData) => { const res = await alerts.create(alertData); execute(); return res; };
  const toggle = async (id, is_active) => { await alerts.toggle(id, is_active); execute(); };
  const remove = async (id) => { await alerts.delete(id); execute(); };

  return { alerts: list, loading, create, toggle, remove };
}

// ─── Forum posts ──────────────────────────────────────────
export function usePosts() {
  const [posts,   setPosts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [,        setPage]    = useState(1);  // page not read directly

  const fetchPosts = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await forums.posts(p);
      if (res.success) {
        if (p === 1) setPosts(res.posts || []);
        else setPosts(prev => [...prev, ...(res.posts || [])]);
      }
    } catch (e) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPosts(1); }, [fetchPosts]);

  // FIX: functional setPage to avoid stale closure on rapid calls
  const loadMore = useCallback(() => {
    setPage(prev => {
      const next = prev + 1;
      fetchPosts(next);
      return next;
    });
  }, [fetchPosts]);

  const likePost = async (id) => {
    const res = await forums.likePost(id);
    if (res.success) {
      setPosts(prev => prev.map(p =>
        p.id === id
          ? { ...p, likes_count: p.likes_count + (res.liked ? 1 : -1), user_liked: res.liked ? 1 : 0 }
          : p
      ));
    }
    return res;
  };

  const savePost = async (id) => {
    const res = await forums.savePost(id);
    if (res.success) {
      setPosts(prev => prev.map(p => p.id === id ? { ...p, user_saved: res.saved ? 1 : 0 } : p));
    }
    return res;
  };

  // v229: createPost now accepts optional media attachment.
  // Backward-compatible: existing callers pass (content, privacy) as before.
  // New callers can pass (content, privacy, media) where media is
  // { url, kind } with kind in {'image','video'}.
  const createPost = async (content, privacy, media = null) => {
    const payload = { content, privacy };
    if (media && media.url) {
      if (media.kind === 'video')      payload.video_url = media.url;
      else if (media.kind === 'image') payload.image_url = media.url;
      payload.media_type = media.kind;
    }
    const res = await forums.createPost(payload);
    if (res.success) setPosts(prev => [res.post, ...prev]);
    return res;
  };

  // v212 Chunk2 fix: delete post also removes from local state.
  // Was missing entirely — ForumsPage called refreshActivityFeed() which
  // only refreshed the activity widget, not the posts feed. Deleted posts
  // stayed visible until manual page refresh.
  const deletePost = async (id) => {
    const res = await forums.deletePost(id);
    if (res?.success !== false) {
      setPosts(prev => prev.filter(p => p.id !== id));
    }
    return res;
  };

  return { posts, loading, likePost, savePost, createPost, deletePost, loadMore };
}

// ─── Notifications ────────────────────────────────────────
export function useNotifications() {
  const [notifs,      setNotifs]  = useState([]);
  const [unreadCount, setUnread]  = useState(0);
  const [loading,     setLoading] = useState(true);
  const retryMs = useRef(60000);

  const fetchNotifs = useCallback(async () => {
    try {
      const res = await notifications.list();
      if (res.success) {
        setNotifs(res.notifications || []);
        setUnread(res.unread_count  || 0);
        retryMs.current = 60000; // reset on success
      }
    } catch (e) {
      // 429 or network errors — back off instead of crashing
      if (e?.message?.includes('429') || e?.message?.includes('Too many')) {
        retryMs.current = Math.min(retryMs.current * 2, 300000); // max 5min backoff
      }
      // NEVER throw — silently fail
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('pc_token');
    if (!token) return; // Don't fire before login

    fetchNotifs();
    // Use dynamic interval with backoff
    let t = null;
    const schedule = () => { t = setTimeout(() => { fetchNotifs().finally(schedule); }, retryMs.current); };
    schedule();
    return () => { if (t) clearTimeout(t); };
  }, [fetchNotifs]);

  // Auto-fetch notifications after login
  useEffect(() => {
    const onLogin = () => {
      retryMs.current = 60000;
      fetchNotifs();
    };
    window.addEventListener('pc_logged_in', onLogin);
    return () => window.removeEventListener('pc_logged_in', onLogin);
  }, [fetchNotifs]);

  const markAllRead = async () => {
    await notifications.readAll();
    setNotifs(prev => prev.map(n => ({ ...n, is_read: 1 })));
    setUnread(0);
  };

  return { notifs, unreadCount, loading, markAllRead, refetch: fetchNotifs };
}

// ─── Global market stats ─────────────────────────────────
export function useGlobalStats() {
  return useAsync(() => market.global());
}

// ─── Crypto news ─────────────────────────────────────────
export function useNews() {
  const [news, setNews]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);

  // Fetch news via REST (used as fallback polling)
  const fetchNews = useCallback(async () => {
    try {
      const r = await market.news();
      if (r?.data?.length > 0) {
        setNews(prev => {
          // Merge: keep existing + add new, dedupe by id, sort newest first
          const existingIds = new Set(prev.map(n => n.id));
          const newOnes = r.data.filter(n => !existingIds.has(n.id));
          const merged = [...newOnes, ...prev].slice(0, 50);
          merged.sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
          return merged;
        });
      }
    } catch(e) { /* polling failed silently */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    let pollingInterval = null;
    let socket = null;
    let wsTimeout = null;
    let hasFetched = false; // prevent duplicate simultaneous fetches

    // ── Attempt Socket.IO WebSocket connection ──────────────
    const startWS = () => {
      try {
        const { getSocket } = require('./useSocket');
        socket = getSocket();

        if (!socket || !socket.connected) {
          startPolling();
          return;
        }

        socket.emit('subscribe_news');
        setWsConnected(true);

        socket.on('news_snapshot', (payload) => {
          if (payload?.data?.length > 0) {
            setNews(payload.data);
            setLoading(false);
          }
        });

        socket.on('news_update', (payload) => {
          if (payload?.data?.length > 0) {
            setNews(prev => {
              const existingIds = new Set(prev.map(n => n.id));
              const newOnes = payload.data.filter(n => !existingIds.has(n.id));
              if (newOnes.length === 0) return prev;
              const merged = [...newOnes, ...prev].slice(0, 50);
              merged.sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
              return merged;
            });
            setLoading(false);
          }
        });

        socket.on('disconnect', () => { setWsConnected(false); startPolling(); });
        socket.on('connect_error', () => { setWsConnected(false); startPolling(); });

        // If no snapshot arrives in 5s, do ONE REST fetch
        wsTimeout = setTimeout(() => {
          if (news.length === 0 && !hasFetched) { hasFetched = true; fetchNews(); startPolling(); }
        }, 5000);

      } catch(err) { startPolling(); }
    };

    const startPolling = () => {
      if (pollingInterval) return;
      // Only fetch if we haven't already done the initial fetch
      if (!hasFetched) { hasFetched = true; fetchNews(); }
      pollingInterval = setInterval(fetchNews, 120000);
    };

    // ONE initial REST fetch — then WS/polling takes over
    if (!hasFetched) { hasFetched = true; fetchNews(); }
    startWS();

    return () => {
      if (wsTimeout) clearTimeout(wsTimeout);
      if (pollingInterval) clearInterval(pollingInterval);
      if (socket) {
        try { socket.off('news_snapshot'); socket.off('news_update'); socket.emit('unsubscribe_news'); } catch(_) {}
      }
    };
  }, [fetchNews]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data: { data: news }, loading, wsConnected };
}

// ─── Toast notifications ─────────────────────────────────
export function useToast() {
  const [toasts, setToasts] = useState([]);

  // S-014: Default variant 'info' (was 'success' — inconsistent with mobile + caused
  // green-success styling for neutral/error messages that lacked explicit variant).
  // Mobile AppContext.js:107 already defaults to 'info'. Now both platforms match.
  const show = useCallback((message, type = 'info') => {
    const id = Date.now();
    setToasts(t => [...t, { id, message, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  }, []);

  return { toasts, show };
}
